/**
 * SF-5127 — the company's readable ServeFirst address (`help_desk_public_address`, for example
 * acme@help.servefirst.ai), shown while ticket emails use it. Customers write to it, so the company
 * publishes it as its support address. Staff can change the part before the @
 * (`help_desk_public_local`, saved on its own); sf-api checks the rules and that no other company
 * has it, and its message is shown when it refuses. The old address stops working at once, so the
 * change asks first. Under it, when the last email came in (HelpDeskLastEmail).
 */
import { useEffect, useState } from 'react';
import { Button, Input, Popconfirm, Space, Typography } from 'antd';
import { HelpDeskRow } from './HelpDeskRow';
import { HelpDeskLastEmail } from './HelpDeskLastEmail';
import type { HelpDeskTabProps } from './HelpDeskTab';
import type { Company } from '../list';

const { Text } = Typography;

const PUBLIC_LOCAL_CHANGE_WARNING =
  'The current address stops working at once. Anyone using it will get no reply.';
const ADDRESS_NOTE = 'Customers write to this address. Only emails sent to it become tickets.';
const LOCAL_RULES_NOTE = 'Letters, numbers and hyphens, 3 to 40 characters.';

/** The part before and after the @ of an address ('' when it has no @). */
const splitAddress = (address: string | null) => {
  const at = address ? address.lastIndexOf('@') : -1;
  return at > 0 && address
    ? { local: address.slice(0, at), host: address.slice(at + 1) }
    : { local: '', host: '' };
};

export const HelpDeskPublicAddress: React.FC<{
  company: Company;
  locked: boolean;
  reason: string | null;
  saver: HelpDeskTabProps['saver'];
}> = ({ company, locked, reason, saver }) => {
  const address = company.help_desk_public_address || null;
  const { local: addressLocal, host } = splitAddress(address);
  const savedLocal = company.help_desk_public_local || addressLocal;
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(savedLocal);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (locked) setConfirm(false);
  }, [locked]);
  const next = local.trim();
  const canSave = !locked && next !== '' && next.toLowerCase() !== savedLocal.toLowerCase();
  const saving = saver.pending?.field === 'help_desk_public_local';

  const saveLocal = async () => {
    if (await saver.save('help_desk_public_local', next, 'ServeFirst address changed')) {
      setEditing(false);
    }
  };

  return (
    <HelpDeskRow
      testId="help-desk-public-address"
      title="ServeFirst address"
      note={reason ?? (editing ? LOCAL_RULES_NOTE : ADDRESS_NOTE)}
    >
      {editing && host ? (
        <Space wrap>
          <Input
            aria-label="Part before the @"
            value={local}
            addonAfter={`@${host}`}
            disabled={locked}
            style={{ width: 360 }}
            onChange={(e) => setLocal(e.target.value)}
          />
          <Popconfirm
            title="Change the ServeFirst address?"
            description={PUBLIC_LOCAL_CHANGE_WARNING}
            okText="Change address"
            open={confirm && canSave}
            onOpenChange={(open) => setConfirm(open && canSave)}
            onConfirm={() => {
              setConfirm(false);
              if (canSave) void saveLocal();
            }}
          >
            <Button type="primary" disabled={!canSave} loading={saving}>
              Save
            </Button>
          </Popconfirm>
          <Button disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </Space>
      ) : (
        <Space wrap>
          {address ? (
            <Text code copyable>
              {address}
            </Text>
          ) : (
            <Text type="warning">Address not made yet</Text>
          )}
          {host ? (
            <Button
              disabled={locked}
              onClick={() => {
                setLocal(savedLocal);
                setEditing(true);
              }}
            >
              Edit
            </Button>
          ) : null}
        </Space>
      )}
      <HelpDeskLastEmail
        at={company.help_desk_last_inbound_at}
        testAddress={address ?? 'the ServeFirst address'}
      />
    </HelpDeskRow>
  );
};
